// Google Ads + GA4 tag wiring.
//
// HOW TO SET UP:
// 1. Luo Google Ads -konversio (Tools → Conversions → New → Website).
//    Saat tunnukset muodossa: AW-1234567890  ja  konversio-etiketti "abcDEFghIJ".
// 2. Aseta nämä joko ympäristömuuttujiin (.env) tai suoraan tähän:
//      VITE_GOOGLE_ADS_ID         = "AW-1234567890"
//      VITE_GOOGLE_ADS_PURCHASE   = "AW-1234567890/abcDEFghIJ"
//      VITE_GA4_ID                = "G-XXXXXXXXXX"   (valinnainen)
// 3. Shopifyn kassalla (montessori24.myshopify.com) ostokonversion mittaus
//    asennetaan Shopify Admin → Sales channels → Google → linkitä sama
//    Google Ads -tili. Tämä sivusto fire-aa kaikki esikonversiot
//    (view_item, add_to_cart, begin_checkout).

export const GOOGLE_ADS_ID = (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) ?? "";
export const GOOGLE_ADS_PURCHASE = (import.meta.env.VITE_GOOGLE_ADS_PURCHASE as string | undefined) ?? "";
export const GA4_ID = (import.meta.env.VITE_GA4_ID as string | undefined) ?? "";

export const ANALYTICS_ENABLED = Boolean(GOOGLE_ADS_ID || GA4_ID);

type GtagArgs = unknown[];
declare global {
  interface Window {
    dataLayer?: GtagArgs[];
    gtag?: (...args: GtagArgs) => void;
  }
}

// ---------------------------------------------------------------------------
// Cookie-consent gate
//
// The CookieConsent banner stores the visitor's choice in localStorage under
// "m24-cookie-consent-v1" and broadcasts "m24:consent-change" whenever it
// changes. GA4 Consent Mode v2 defaults (denied) live in the inline bootstrap
// in src/routes/__root.tsx; this module-level gate is the second layer: no
// analytics event is ever pushed to the dataLayer unless the visitor has
// actively granted analytics consent. Consent/config commands pushed by the
// bootstrap are unaffected.
// ---------------------------------------------------------------------------
const CONSENT_STORAGE_KEY = "m24-cookie-consent-v1";

function readAnalyticsConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { analytics?: boolean } | null;
    return parsed?.analytics === true;
  } catch {
    return false;
  }
}

let analyticsConsent = readAnalyticsConsent();
let marketingConsent = (() => {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    return (JSON.parse(raw) as { marketing?: boolean } | null)?.marketing === true;
  } catch {
    return false;
  }
})();
if (typeof window !== "undefined") {
  window.addEventListener("m24:consent-change", (e) => {
    const detail = (e as CustomEvent<{ analytics?: boolean; marketing?: boolean }>).detail;
    analyticsConsent = detail?.analytics === true;
    marketingConsent = detail?.marketing === true;
  });
}

function gtag(...args: GtagArgs) {
  if (typeof window === "undefined") return;
  if (!window.gtag) return;
  window.gtag(...args);
}

/** Push a GA4 event, respecting the visitor's cookie-consent choice. */
function gtagEvent(name: string, params: Record<string, unknown>) {
  if (!analyticsConsent) return;
  gtag("event", name, params);
}

/** Set GA4 user properties — gated by analytics consent like events. */
export function setGtagUserProperties(props: Record<string, unknown>) {
  if (!analyticsConsent) return;
  gtag("set", "user_properties", props);
}

/**
 * Generic GA4 event helper for UI interactions (hero CTAs, product rail
 * clicks, category tiles). Safe to call from SSR; becomes a no-op if gtag
 * isn't loaded or analytics isn't configured.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  gtagEvent(name, params);
}

export function trackHeroCta(params: {
  variant: string;
  destination: string;
  label: string;
}) {
  trackEvent("hero_cta_click", {
    experiment: "hero_v1",
    variant: params.variant,
    destination: params.destination,
    label: params.label,
  });
}

export function trackRailInteraction(params: {
  rail: string;
  action: "product_click" | "view_all" | "scroll";
  product_id?: string;
  product_name?: string;
  position?: number;
}) {
  trackEvent("rail_interaction", params);
}

// ---------------------------------------------------------------------------
// GA4 ecommerce (standard events & item schema)
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
// ---------------------------------------------------------------------------

/** One purchasable line, as our components see it. */
export type EcomLine = {
  id: string;
  name: string;
  price: number;
  quantity: number;
  currency?: string;
  brand?: string;
  category?: string;
  variant?: string;
};

/** GA4 item object. */
type Ga4Item = {
  item_id: string;
  item_name: string;
  item_category: string;
  item_brand?: string;
  item_variant?: string;
  price: number;
  quantity: number;
  index: number;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Map a product to a stable Finnish category name for GA4 item_category.
 * Works off the title alone (list queries don't fetch productType/tags) but
 * uses productType/tags when the caller has them (product detail page).
 */
export function ga4ItemCategory(
  title: string,
  productType?: string,
  tags?: string[],
): string {
  const s = `${productType ?? ""} ${(tags ?? []).join(" ")} ${title}`.toLowerCase();
  if (/oppimistorni|helper\s*tower|learning\s*tower|kitchen\s*tower|seisontajakkara|apujakkara/.test(s))
    return "Oppimistornit";
  if (/pikler|kiipeilykolmio|climbing\s*triangle/.test(s)) return "Pikler-kolmiot";
  if (/kiipeily|climbing|ramppi|\bramp\b|\barch\b|kaari/.test(s)) return "Kiipeilytelineet";
  if (/iglu|igloo|leikkimaja|play\s*house/.test(s)) return "Iglut & leikkimajat";
  if (/vaaterekki|vaateteline|clothing\s*rack|clothes\s*rail/.test(s))
    return "Lasten vaaterekit";
  if (/sänky|sanky|patja|\bbed\b|mattress/.test(s)) return "Montessori-sängyt";
  if (/pallomeri|ball\s?pit/.test(s)) return "Pallomeret";
  if (/tasapaino|balance/.test(s)) return "Tasapaino & motoriikka";
  if (/lelu|lelut|\btoy|palapeli|puzzle|\bpeli\b|\bgame/.test(s)) return "Montessori-lelut";
  return productType?.trim() || "Montessori-tuotteet";
}

function toGa4Items(lines: EcomLine[]): Ga4Item[] {
  return lines.map((l, i) => ({
    item_id: l.id,
    item_name: l.name,
    item_category: l.category ?? ga4ItemCategory(l.name),
    ...(l.brand ? { item_brand: l.brand } : {}),
    ...(l.variant ? { item_variant: l.variant } : {}),
    price: round2(l.price),
    quantity: l.quantity,
    index: i,
  }));
}

/** Visitor opened a product detail page. */
export function trackViewItem(params: {
  id: string;
  name: string;
  price: number;
  currency?: string;
  brand?: string;
  category?: string;
  variant?: string;
}) {
  gtagEvent("view_item", {
    currency: params.currency ?? "EUR",
    value: round2(params.price),
    items: toGa4Items([{ ...params, quantity: 1 }]),
  });
}

/** Visitor clicked a product in a list / rail / search result. */
export function trackSelectItem(params: {
  id: string;
  name: string;
  price: number;
  currency?: string;
  brand?: string;
  category?: string;
  listName?: string;
  index?: number;
}) {
  const items = toGa4Items([{ ...params, quantity: 1 }]);
  if (params.index != null) items[0].index = params.index;
  gtagEvent("select_item", {
    item_list_name: params.listName,
    currency: params.currency ?? "EUR",
    value: round2(params.price),
    items,
  });
}

export function trackAddToCart(params: {
  id: string;
  name: string;
  price: number;
  quantity: number;
  currency?: string;
  brand?: string;
  category?: string;
  variant?: string;
}) {
  gtagEvent("add_to_cart", {
    currency: params.currency ?? "EUR",
    value: round2(params.price * params.quantity),
    items: toGa4Items([params]),
  });
}

/** Visitor removed a line from the cart (incl. quantity → 0). */
export function trackRemoveFromCart(params: {
  id: string;
  name: string;
  price: number;
  quantity: number;
  currency?: string;
  brand?: string;
  category?: string;
  variant?: string;
}) {
  gtagEvent("remove_from_cart", {
    currency: params.currency ?? "EUR",
    value: round2(params.price * params.quantity),
    items: toGa4Items([params]),
  });
}

/** Visitor opened the cart drawer with items in it. */
export function trackViewCart(params: {
  value: number;
  currency?: string;
  items: EcomLine[];
}) {
  gtagEvent("view_cart", {
    currency: params.currency ?? "EUR",
    value: round2(params.value),
    items: toGa4Items(params.items),
  });
}

export function trackBeginCheckout(params: {
  value: number;
  currency?: string;
  items: EcomLine[];
}) {
  gtagEvent("begin_checkout", {
    currency: params.currency ?? "EUR",
    value: round2(params.value),
    items: toGa4Items(params.items),
  });
  // Optional Google Ads conversion for "started checkout" — useful for Smart Bidding signals.
  // Advertising tags require marketing consent, independent of analytics.
  if (GOOGLE_ADS_PURCHASE && marketingConsent) {
    gtag("event", "conversion", {
      send_to: GOOGLE_ADS_PURCHASE,
      value: params.value,
      currency: params.currency ?? "EUR",
    });
  }
}